import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  DollarSign, Plus, Search, Filter, Download, 
  CheckCircle, Clock, Edit, Trash2, Eye, X, ArrowLeft, FileText, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const SuspenseAmount = () => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState({
    total_suspense_amount: 0,
    suspense_count: 0,
    total_updated_amount: 0,
    updated_count: 0
  });
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showViewDetailsModal, setShowViewDetailsModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Form states
  const [newEntry, setNewEntry] = useState({
    transaction_id: '',
    bank_name: '',
    transaction_date: '',
    amount: '',
    notes: ''
  });

  const [updateData, setUpdateData] = useState({
    customer_name: '',
    customer_email: '',
    customer_id: '',
    invoice_number: '',
    notes: ''
  });

  // Fetch entries
  const fetchEntries = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('customerAdminToken');
      const statusParam = filterStatus !== 'all' ? `?status=${filterStatus}` : '';
      
      const response = await fetch(`${API_BASE_URL}/api/suspense/entries${statusParam}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success) {
        setEntries(data.entries || []);
        setSummary(data.summary || {});
      }
    } catch (error) {
      console.error('Error fetching entries:', error);
      toast.error('Failed to load suspense entries');
    } finally {
      setLoading(false);
    }
  };

  // Create new entry
  const handleCreateEntry = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const token = localStorage.getItem('customerAdminToken');
      
      const response = await fetch(`${API_BASE_URL}/api/suspense/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: new URLSearchParams(newEntry)
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Suspense entry created successfully');
        setShowAddModal(false);
        setNewEntry({
          transaction_id: '',
          bank_name: '',
          transaction_date: '',
          amount: '',
          notes: ''
        });
        fetchEntries();
      } else {
        toast.error(data.message || 'Failed to create entry');
      }
    } catch (error) {
      console.error('Error creating entry:', error);
      toast.error('Failed to create entry');
    } finally {
      setLoading(false);
    }
  };

  // Update entry
  const handleUpdateEntry = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const token = localStorage.getItem('customerAdminToken');
      
      const response = await fetch(
        `${API_BASE_URL}/api/suspense/update/${selectedEntry.entry_id}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/x-www-form-urlencoded'
          },
          body: new URLSearchParams(updateData)
        }
      );
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Entry updated successfully');
        setShowUpdateModal(false);
        setSelectedEntry(null);
        setUpdateData({
          customer_name: '',
          customer_email: '',
          customer_id: '',
          invoice_number: '',
          notes: ''
        });
        fetchEntries();
      } else {
        toast.error(data.message || 'Failed to update entry');
      }
    } catch (error) {
      console.error('Error updating entry:', error);
      toast.error('Failed to update entry');
    } finally {
      setLoading(false);
    }
  };

  // Delete entry
  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;
    
    try {
      const token = localStorage.getItem('customerAdminToken');
      const response = await fetch(`${API_BASE_URL}/api/suspense/delete/${entryId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Entry deleted successfully');
        fetchEntries();
      }
    } catch (error) {
      console.error('Error deleting entry:', error);
      toast.error('Failed to delete entry');
    }
  };

  // Export to CSV
  const handleExportData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('customerAdminToken');
      const statusParam = filterStatus !== 'all' ? `?status=${filterStatus}` : '';
      
      const response = await fetch(`${API_BASE_URL}/api/suspense/export${statusParam}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `suspense_entries_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Data exported successfully');
      } else {
        toast.error('Failed to export data');
      }
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error('Failed to export data');
    } finally {
      setLoading(false);
    }
  };

  // Download Receipt
  const handleDownloadReceipt = (entry) => {
    const receiptWindow = window.open('', '_blank');
    const receiptContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Payment Receipt - ${entry.transaction_id}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
          .header { text-align: center; border-bottom: 3px solid #047857; padding-bottom: 20px; margin-bottom: 30px; }
          .logo { color: #047857; font-size: 32px; font-weight: bold; }
          .receipt-title { color: #047857; font-size: 24px; margin-top: 10px; }
          .info-section { margin: 20px 0; }
          .info-row { display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #e5e7eb; }
          .label { font-weight: bold; color: #374151; }
          .value { color: #1f2937; }
          .amount-box { background: #d1fae5; padding: 20px; text-align: center; margin: 30px 0; border-radius: 8px; }
          .amount { font-size: 36px; font-weight: bold; color: #047857; }
          .footer { margin-top: 40px; padding-top: 20px; border-top: 2px solid #e5e7eb; text-align: center; color: #6b7280; }
          .status-badge { display: inline-block; padding: 5px 15px; border-radius: 20px; font-size: 14px; font-weight: bold; }
          .status-updated { background: #d1fae5; color: #047857; }
          .status-suspense { background: #fee2e2; color: #dc2626; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo">NETIX FIBERNET</div>
          <div class="receipt-title">Payment Receipt</div>
        </div>
        
        <div class="info-section">
          <div class="info-row">
            <span class="label">Receipt No:</span>
            <span class="value">${entry.entry_id}</span>
          </div>
          <div class="info-row">
            <span class="label">Transaction ID:</span>
            <span class="value">${entry.transaction_id}</span>
          </div>
          <div class="info-row">
            <span class="label">Bank Name:</span>
            <span class="value">${entry.bank_name}</span>
          </div>
          <div class="info-row">
            <span class="label">Transaction Date:</span>
            <span class="value">${formatDate(entry.transaction_date)}</span>
          </div>
          <div class="info-row">
            <span class="label">Entry Created:</span>
            <span class="value">${formatDate(entry.created_at)}</span>
          </div>
          <div class="info-row">
            <span class="label">Status:</span>
            <span class="value">
              <span class="status-badge ${entry.status === 'updated' ? 'status-updated' : 'status-suspense'}">
                ${entry.status === 'updated' ? 'Already Updated' : 'In Suspense'}
              </span>
            </span>
          </div>
        </div>
        
        <div class="amount-box">
          <div style="color: #6b7280; margin-bottom: 10px;">Amount</div>
          <div class="amount">${formatCurrency(entry.amount)}</div>
        </div>
        
        ${entry.customer_details ? `
          <div class="info-section">
            <h3 style="color: #047857; margin-bottom: 15px;">Customer Details</h3>
            <div class="info-row">
              <span class="label">Customer Name:</span>
              <span class="value">${entry.customer_details.name}</span>
            </div>
            <div class="info-row">
              <span class="label">Customer Email:</span>
              <span class="value">${entry.customer_details.email}</span>
            </div>
            <div class="info-row">
              <span class="label">Customer ID:</span>
              <span class="value">${entry.customer_details.customer_id}</span>
            </div>
            <div class="info-row">
              <span class="label">Invoice Number:</span>
              <span class="value">${entry.invoice_number}</span>
            </div>
            ${entry.adjustment_notes ? `
              <div class="info-row">
                <span class="label">Notes:</span>
                <span class="value">${entry.adjustment_notes}</span>
              </div>
            ` : ''}
          </div>
        ` : ''}
        
        ${entry.notes ? `
          <div class="info-section">
            <h3 style="color: #047857; margin-bottom: 15px;">Additional Notes</h3>
            <p style="color: #1f2937;">${entry.notes}</p>
          </div>
        ` : ''}
        
        <div class="footer">
          <p>NETIX FIBERNET - Customer Management Portal</p>
          <p>This is a computer-generated receipt and does not require a signature.</p>
          <p style="margin-top: 10px; font-size: 12px;">Generated on: ${new Date().toLocaleString('en-IN')}</p>
        </div>
        
        <script>
          window.onload = function() { window.print(); }
        </script>
      </body>
      </html>
    `;
    
    receiptWindow.document.write(receiptContent);
    receiptWindow.document.close();
  };

  useEffect(() => {
    fetchEntries();
  }, [filterStatus]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const filteredEntries = entries.filter(entry => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      entry.transaction_id?.toLowerCase().includes(search) ||
      entry.bank_name?.toLowerCase().includes(search) ||
      entry.customer_details?.name?.toLowerCase().includes(search) ||
      entry.invoice_number?.toLowerCase().includes(search)
    );
  });

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button 
            onClick={() => navigate('/customer-admin/dashboard')}
            variant="outline"
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Billing
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Suspense Amount</h1>
            <p className="text-gray-600 mt-1">Manage unallocated payments and adjustments</p>
          </div>
        </div>
        <Button 
          onClick={() => setShowAddModal(true)}
          className="bg-blue-600 hover:bg-blue-700"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add New Entry
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="border-2 border-red-200 bg-red-50">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-red-700 flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Total Suspense Amount
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-red-600">
              {formatCurrency(summary.total_suspense_amount || 0)}
            </div>
            <p className="text-sm text-red-600 mt-1">
              {summary.suspense_count || 0} entries pending
            </p>
          </CardContent>
        </Card>

        <Card className="border-2 border-green-200 bg-green-50">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-green-700 flex items-center gap-2">
              <CheckCircle className="h-4 w-4" />
              Already Updated Amount
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">
              {formatCurrency(summary.total_updated_amount || 0)}
            </div>
            <p className="text-sm text-green-600 mt-1">
              {summary.updated_count || 0} entries updated
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by Transaction ID, Bank, Customer, Invoice..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Status Filter */}
            <div className="flex gap-2">
              <Button
                variant={filterStatus === 'all' ? 'default' : 'outline'}
                onClick={() => setFilterStatus('all')}
                size="sm"
              >
                All
              </Button>
              <Button
                variant={filterStatus === 'in_suspense' ? 'default' : 'outline'}
                onClick={() => setFilterStatus('in_suspense')}
                size="sm"
                className={filterStatus === 'in_suspense' ? 'bg-red-600' : ''}
              >
                In Suspense
              </Button>
              <Button
                variant={filterStatus === 'updated' ? 'default' : 'outline'}
                onClick={() => setFilterStatus('updated')}
                size="sm"
                className={filterStatus === 'updated' ? 'bg-green-600' : ''}
              >
                Updated
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Entries Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>All Entries ({filteredEntries.length})</span>
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleExportData}
              disabled={loading || filteredEntries.length === 0}
            >
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-600 mt-2">Loading...</p>
            </div>
          ) : filteredEntries.length === 0 ? (
            <div className="text-center py-8">
              <DollarSign className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-600">No suspense entries found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Entry Time</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Transaction ID</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bank</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Trans. Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredEntries.map((entry) => (
                    <tr key={entry.entry_id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {formatDate(entry.created_at)}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-blue-600">
                        {entry.transaction_id}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {entry.bank_name}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {formatDate(entry.transaction_date)}
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                        {formatCurrency(entry.amount)}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {entry.status === 'in_suspense' ? (
                          <span className="px-3 py-1 text-xs font-semibold text-red-700 bg-red-100 rounded-full">
                            In Suspense
                          </span>
                        ) : (
                          <span className="px-3 py-1 text-xs font-semibold text-green-700 bg-green-100 rounded-full">
                            Already Updated
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {entry.customer_details ? (
                          <div>
                            <div className="font-medium text-gray-900">
                              {entry.customer_details.name}
                            </div>
                            <div className="text-xs text-gray-500">
                              Invoice: {entry.invoice_number}
                            </div>
                          </div>
                        ) : (
                          <span className="text-gray-400">Not allocated</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex gap-2">
                          {/* Download Receipt */}
                          <button
                            onClick={() => handleDownloadReceipt(entry)}
                            className="text-green-600 hover:text-green-800"
                            title="Download Receipt"
                          >
                            <Download className="h-4 w-4" />
                          </button>
                          
                          {/* View Details for Updated Entries */}
                          {entry.status === 'updated' && (
                            <button
                              onClick={() => {
                                setSelectedEntry(entry);
                                setShowViewDetailsModal(true);
                              }}
                              className="text-purple-600 hover:text-purple-800"
                              title="View Updated Details"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                          )}
                          
                          {/* Edit/Update Button */}
                          <button
                            onClick={() => {
                              setSelectedEntry(entry);
                              setShowUpdateModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-800"
                            title={entry.status === 'updated' ? 'Edit/Transfer Entry' : 'Update Entry'}
                          >
                            {entry.status === 'updated' ? <RefreshCw className="h-4 w-4" /> : <Edit className="h-4 w-4" />}
                          </button>
                          
                          {/* Delete Button */}
                          <button
                            onClick={() => handleDeleteEntry(entry.entry_id)}
                            className="text-red-600 hover:text-red-800"
                            title="Delete Entry"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Entry Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-bold">Add New Suspense Entry</h2>
              <button onClick={() => setShowAddModal(false)}>
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleCreateEntry} className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Transaction ID <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={newEntry.transaction_id}
                  onChange={(e) => setNewEntry({...newEntry, transaction_id: e.target.value})}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter transaction ID"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bank Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={newEntry.bank_name}
                  onChange={(e) => setNewEntry({...newEntry, bank_name: e.target.value})}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., State Bank of India"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Transaction Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  required
                  value={newEntry.transaction_date}
                  onChange={(e) => setNewEntry({...newEntry, transaction_date: e.target.value})}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Amount (₹) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={newEntry.amount}
                  onChange={(e) => setNewEntry({...newEntry, amount: e.target.value})}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes
                </label>
                <textarea
                  value={newEntry.notes}
                  onChange={(e) => setNewEntry({...newEntry, notes: e.target.value})}
                  rows="3"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Add any additional notes about this transaction..."
                />
              </div>
              <div className="flex gap-3 pt-4 border-t">
                <Button type="submit" disabled={loading} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5">
                  {loading ? 'Creating...' : 'Create Entry'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowAddModal(false)} className="px-6">
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Details Modal */}
      {showViewDetailsModal && selectedEntry && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b sticky top-0 bg-white">
              <h2 className="text-xl font-bold text-green-700">✓ Payment Updated Details</h2>
              <button onClick={() => setShowViewDetailsModal(false)}>
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {/* Transaction Details */}
              <div className="bg-green-50 border-2 border-green-200 p-4 rounded-lg">
                <h3 className="text-sm font-semibold text-green-800 mb-3">Transaction Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Transaction ID:</p>
                    <p className="font-semibold">{selectedEntry.transaction_id}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Bank Name:</p>
                    <p className="font-semibold">{selectedEntry.bank_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Transaction Date:</p>
                    <p className="font-semibold">{formatDate(selectedEntry.transaction_date)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Amount:</p>
                    <p className="font-semibold text-lg text-green-700">{formatCurrency(selectedEntry.amount)}</p>
                  </div>
                </div>
              </div>

              {/* Customer Details */}
              {selectedEntry.customer_details && (
                <div className="bg-blue-50 border-2 border-blue-200 p-4 rounded-lg">
                  <h3 className="text-sm font-semibold text-blue-800 mb-3">✓ Payment Allocated To</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Customer Name:</p>
                      <p className="font-semibold">{selectedEntry.customer_details.name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Customer Email:</p>
                      <p className="font-semibold">{selectedEntry.customer_details.email}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Customer ID:</p>
                      <p className="font-semibold">{selectedEntry.customer_details.customer_id}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Invoice Number:</p>
                      <p className="font-semibold text-blue-700">{selectedEntry.invoice_number}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Update History */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-sm font-semibold text-gray-800 mb-3">Update History</h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Created At:</span>
                    <span className="font-medium">{formatDate(selectedEntry.created_at)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Created By:</span>
                    <span className="font-medium">{selectedEntry.created_by}</span>
                  </div>
                  {selectedEntry.updated_at && (
                    <>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Updated At:</span>
                        <span className="font-medium">{formatDate(selectedEntry.updated_at)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Updated By:</span>
                        <span className="font-medium">{selectedEntry.updated_by}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Notes */}
              {(selectedEntry.notes || selectedEntry.adjustment_notes) && (
                <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
                  <h3 className="text-sm font-semibold text-yellow-800 mb-2">Notes</h3>
                  <p className="text-sm text-gray-700">{selectedEntry.adjustment_notes || selectedEntry.notes}</p>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t">
                <Button 
                  onClick={() => {
                    setShowViewDetailsModal(false);
                    setShowUpdateModal(true);
                  }}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Transfer to Another Customer
                </Button>
                <Button 
                  onClick={() => handleDownloadReceipt(selectedEntry)}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download Receipt
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowViewDetailsModal(false)}
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Update Entry Modal */}
      {showUpdateModal && selectedEntry && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b sticky top-0 bg-white">
              <h2 className="text-xl font-bold">
                {selectedEntry.status === 'updated' ? 'Transfer Payment to Another Customer' : 'Update Suspense Entry'}
              </h2>
              <button onClick={() => setShowUpdateModal(false)}>
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                <p className="text-sm text-gray-600">Transaction ID:</p>
                <p className="font-semibold">{selectedEntry.transaction_id}</p>
                <p className="text-sm text-gray-600 mt-2">Amount:</p>
                <p className="font-semibold text-lg">{formatCurrency(selectedEntry.amount)}</p>
              </div>
              
              {selectedEntry.status === 'updated' && selectedEntry.customer_details && (
                <div className="bg-yellow-50 border border-yellow-300 p-4 rounded-lg">
                  <p className="text-sm font-semibold text-yellow-800 mb-2">⚠️ Currently Allocated To:</p>
                  <p className="text-sm text-gray-700">
                    <strong>{selectedEntry.customer_details.name}</strong><br/>
                    Invoice: {selectedEntry.invoice_number}
                  </p>
                  <p className="text-xs text-yellow-700 mt-2">
                    Updating will transfer this payment to a different customer
                  </p>
                </div>
              )}
              <form onSubmit={handleUpdateEntry} className="space-y-5">
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 border-b pb-2">New Customer Details</h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Customer Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={updateData.customer_name}
                      onChange={(e) => setUpdateData({...updateData, customer_name: e.target.value})}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter customer name"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Customer Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      required
                      value={updateData.customer_email}
                      onChange={(e) => setUpdateData({...updateData, customer_email: e.target.value})}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="customer@example.com"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Customer ID <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={updateData.customer_id}
                      onChange={(e) => setUpdateData({...updateData, customer_id: e.target.value})}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g., CUST001"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Invoice Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={updateData.invoice_number}
                      onChange={(e) => setUpdateData({...updateData, invoice_number: e.target.value})}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g., INV-2024-001"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Adjustment Notes
                    </label>
                    <textarea
                      value={updateData.notes}
                      onChange={(e) => setUpdateData({...updateData, notes: e.target.value})}
                      rows="3"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Add any additional notes about this payment allocation..."
                    />
                  </div>
                </div>
                
                <div className="flex gap-3 pt-4 border-t">
                  <Button type="submit" disabled={loading} className="flex-1 bg-green-600 hover:bg-green-700 text-white font-medium py-2.5">
                    {loading ? 'Updating...' : (selectedEntry.status === 'updated' ? 'Transfer Payment' : 'Update Entry')}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowUpdateModal(false)} className="px-6">
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuspenseAmount;
